'use client';

import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { Modal, BottomSheet } from '@paeffgen/react/crm-documents/ui';
import {
  type Document,
  DocumentType as DocumentTypeEnum,
  SET_DOCUMENTS_READ_MUTATION,
} from '@paeffgen/react/crm-documents/data-access-graphql';
import { useAdmin } from '@paeffgen/react/crm-documents/feature-auth';
import {
  TYPE_LABELS,
  TYPE_BADGES,
  TYPE_SHORT,
  useIsDesktop,
} from '@paeffgen/react/crm-documents/util';
import { clsx } from 'clsx';
import { DocumentDetail } from './document-detail';
import { OrderOfferButton } from './order-offer-button';
import {
  MobileDocumentDetail,
  MobileDocumentDetailFooter,
} from './mobile-document-detail';

interface DocumentDetailDialogProps {
  document: Document | null;
  isOpen: boolean;
  onClose: () => void;
  /** Articlenumber to visually highlight inside the positions list — used when
   *  the dialog is opened from a context that already pinpoints one article
   *  (e.g. the article search). */
  highlightArticleNumber?: string | null;
}

export function DocumentDetailDialog({
  document,
  isOpen,
  onClose,
  highlightArticleNumber,
}: DocumentDetailDialogProps) {
  const isDesktop = useIsDesktop();
  const { adminQueryVars } = useAdmin();

  // Selection lives here so MobileDocumentDetail (body) and
  // MobileDocumentDetailFooter (footer slot) can share the same Set
  // without context plumbing through BottomSheet's children/footer split.
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    setSelected(new Set());
  }, [document?.id]);

  // Auto-"gelesen" beim Öffnen (Posteingang-Modell): einmal pro geöffnetem
  // Beleg, nur wenn aktuell ungelesen. Das zurückgegebene id+isRead aktualisiert
  // die Listenzeile über den normalisierten Apollo-Cache; das Dashboard wird
  // (sofern gemountet) per refetch nachgezogen.
  const [markRead] = useMutation(SET_DOCUMENTS_READ_MUTATION);
  const docId = document?.id;
  const docUnread = document?.isRead === false;
  useEffect(() => {
    if (!isOpen || !docId || !docUnread) return;
    void markRead({
      variables: { documentIds: [docId], read: true, ...adminQueryVars },
      optimisticResponse: {
        setDocumentsRead: [{ __typename: 'Document', id: docId, isRead: true }],
      },
      refetchQueries: ['DashboardStats'],
    });
    // adminQueryVars/markRead sind stabil genug; bewusst nur auf Öffnen +
    // Belegwechsel reagieren, nicht auf jede isRead-Änderung.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, docId]);

  if (!document) return null;

  const title = `${TYPE_LABELS[document.type]} ${document.documentNumber}`;

  return (
    <>
      <Modal
        isOpen={isOpen && isDesktop}
        onClose={onClose}
        title={title}
        className="max-w-7xl"
        headerActions={
          document.type === DocumentTypeEnum.ANGEBOT ? (
            <OrderOfferButton document={document} />
          ) : undefined
        }
      >
        <DocumentDetail
          key={document.id}
          document={document}
          highlightArticleNumber={highlightArticleNumber}
          selected={selected}
          setSelected={setSelected}
        />
      </Modal>

      <BottomSheet
        open={isOpen && !isDesktop}
        onClose={onClose}
        ariaLabel={title}
        contentKey={document.id}
        header={
          <div className="flex items-center gap-3 px-4 py-3">
            <span
              className={clsx(
                'w-10 h-10 rounded-lg border flex items-center justify-center text-sm font-bold uppercase tracking-wide shrink-0',
                TYPE_BADGES[document.type]
              )}
            >
              {TYPE_SHORT[document.type]}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
                {TYPE_LABELS[document.type]}
              </div>
              <div className="text-lg font-extrabold tabular-nums text-gray-900 truncate">
                {document.documentNumber}
              </div>
            </div>
          </div>
        }
        footer={
          <MobileDocumentDetailFooter
            document={document}
            selected={selected}
            setSelected={setSelected}
          />
        }
      >
        <MobileDocumentDetail
          key={document.id}
          document={document}
          onClose={onClose}
          selected={selected}
          setSelected={setSelected}
          highlightArticleNumber={highlightArticleNumber}
        />
      </BottomSheet>
    </>
  );
}
